import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysUser } from '../queryKeys';
import { type UserIdentityProviderLinksAdmin } from '../types';
import { isExpectedUserIdentityProviderLinkError } from '../userIdentityProviderLinkError';

import { fetchUserIdentityProviderLinksAdmin } from './fetchUserIdentityProviderLinksAdmin';

const MAX_RETRIES = 2;

export const selectUserIdentityProviderLinks = ({
  data,
}: UserIdentityProviderLinksAdmin) => data;

/**
 * Generates the query options for a user's single sign-on links.
 *
 * @param userId - The ID of the user (or undefined to skip).
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
// fallow-ignore-next-line unused-export -- the options factory is the entry point for loaders and prefetching
export const getUserIdentityProviderLinksAdminQueryOptions = <
  TData = UserIdentityProviderLinksAdmin,
>({
  userId,
  options,
}: {
  userId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<UserIdentityProviderLinksAdmin, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: userId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchUserIdentityProviderLinksAdmin(userId, { signal })
      : skipToken,
    queryKey: queryKeysUser.getIdentityProviderLinksAdmin(userId ?? ''),
    // An expected answer, such as a user without a login, is final: no retry changes it.
    retry: (failureCount, error) =>
      !isExpectedUserIdentityProviderLinkError(error) &&
      failureCount < MAX_RETRIES,
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query hook for the single sign-on links of a user's login. Requires ADMIN access.
 * The query is skipped when no `userId` is provided.
 *
 * @param userId - The ID of the user (or undefined to skip).
 * @param options - Additional TanStack Query options.
 * @returns The user's links, one per identity provider.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-User/operation/list_identity_provider_links_vadmin_users__user_id__identity_provider_links_get
 */
export const useQueryUserIdentityProviderLinksAdmin = <
  TData = UserIdentityProviderLinksAdmin,
>(
  userId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<UserIdentityProviderLinksAdmin, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getUserIdentityProviderLinksAdminQueryOptions<TData>({ options, userId }),
  );
